import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box, Button, Checkbox, CircularProgress, FormControlLabel, IconButton, LinearProgress, Paper, TextField, Tooltip, Typography
} from '@mui/material';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import TuneIcon from '@mui/icons-material/Tune';
import RefreshIcon from '@mui/icons-material/Refresh';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import TableChartOutlinedIcon from '@mui/icons-material/TableChartOutlined';
import CashReport from '../reports_doc/CashReport';
import BankReport from '../reports_doc/BankReport';
import { usePermissions } from '../../../utils/usePermissions';
import { ReportSecurityWrapper } from './ReportSecurityWrapper';
import { notify } from '../../notifications';
import { apiErrorMessage, EditPharmacy } from './edit/editApi';
import { formatDateRu, getTodayStr } from './edit/editCalc';
import { ConfirmDialog, EditEmptyState, PharmacyAutocomplete, ScopeChip } from './edit/EditCommon';
import SummaryTable from './summary/SummaryTable';
import {
  MIN_REPORT_DATE, SummaryAuditKind, SummaryResponse, auditSummary, clearLegacyCache, currentMonthPeriod,
  fetchSummary, isPeriodValid, loadFilters, prevMonthPeriod, saveFilters, useSummaryUsers
} from './summary/summaryApi';
import { exportSummaryExcel, exportSummaryExcelColumns, toDocumentRows } from './summary/summaryExport';
import { ACTION_BUTTON_SX } from './summary/summaryStyles';

const BORDER = '1px solid #e2e8f0';
const DATE_FIELD = 'date';
/** Пауза после последнего изменения фильтров: поле даты порождает события при каждой набранной цифре года */
const FETCH_DEBOUNCE_MS = 300;

const PeriodReport: React.FC = () => {
  const { hasPerm } = usePermissions();
  const canExportExcel = hasPerm('reports.view_summary.excel_export');
  const canCashReport = hasPerm('reports.view_summary.cash_report');
  const canBankReport = hasPerm('reports.view_summary.bank_report');
  const canProtectCopy = hasPerm('reports.view_summary.protect_copy');

  const { data: usersData, loading: usersLoading, error: usersError, reload: reloadUsers } = useSummaryUsers();

  const initialPeriod = useMemo(currentMonthPeriod, []);
  const [pharmacy, setPharmacy] = useState<EditPharmacy | null>(null);
  const [startDate, setStartDate] = useState(initialPeriod.startDate);
  const [endDate, setEndDate] = useState(initialPeriod.endDate);

  const [report, setReport] = useState<SummaryResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [reloadKey, setReloadKey] = useState(0);

  const [columnsOpen, setColumnsOpen] = useState(false);
  const [selectedColumns, setSelectedColumns] = useState<string[]>([]);
  const [exporting, setExporting] = useState(false);

  // Автозагрузка после восстановления выбора и ручное обновление не пишутся в журнал как новый запрос отчета
  const silentNextRef = useRef(false);
  const restoredRef = useRef(false);
  const today = getTodayStr();

  useEffect(() => {
    document.title = 'Просмотр отчета | Zerde';
    clearLegacyCache();
  }, []);

  // Выбор аптеки и периода запоминается на время работы вкладки браузера; данные - нет, они всегда берутся у сервера
  useEffect(() => {
    if (restoredRef.current || !usersData) return;
    restoredRef.current = true;
    const saved = loadFilters();
    if (!saved) return;
    const match = usersData.users.filter((u) => u.username === saved.username)[0];
    if (!match) return;
    silentNextRef.current = true;
    setPharmacy(match);
    if (isPeriodValid(saved.startDate, saved.endDate)) {
      setStartDate(saved.startDate);
      setEndDate(saved.endDate);
    }
  }, [usersData]);

  const periodOk = isPeriodValid(startDate, endDate);

  useEffect(() => {
    if (!pharmacy || !periodOk) {
      setReport(null);
      setError('');
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError('');
    const timer = setTimeout(() => {
      const silent = silentNextRef.current;
      silentNextRef.current = false;
      fetchSummary(pharmacy.username, { startDate, endDate }, silent)
        .then((res) => {
          if (!cancelled) setReport(res);
        })
        .catch((err) => {
          if (cancelled) return;
          setReport(null);
          setError(apiErrorMessage(err, 'Не удалось загрузить отчет'));
        })
        .then(() => {
          if (!cancelled) setLoading(false);
        });
    }, FETCH_DEBOUNCE_MS);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [pharmacy, startDate, endDate, periodOk, reloadKey]);

  const applyFilters = useCallback((nextPharmacy: EditPharmacy | null, nextStart: string, nextEnd: string) => {
    silentNextRef.current = false;
    setPharmacy(nextPharmacy);
    setStartDate(nextStart);
    setEndDate(nextEnd);
    saveFilters(nextPharmacy ? { username: nextPharmacy.username, startDate: nextStart, endDate: nextEnd } : null);
  }, []);

  const handlePharmacyChange = (next: EditPharmacy | null) => applyFilters(next, startDate, endDate);
  const handleStartChange = (e: React.ChangeEvent<HTMLInputElement>) => applyFilters(pharmacy, e.target.value, endDate);
  const handleEndChange = (e: React.ChangeEvent<HTMLInputElement>) => applyFilters(pharmacy, startDate, e.target.value);
  const handlePreset = (period: { startDate: string; endDate: string }) => applyFilters(pharmacy, period.startDate, period.endDate);

  const handleRefresh = () => {
    silentNextRef.current = true;
    setReloadKey((k) => k + 1);
  };

  const handleRetry = () => {
    silentNextRef.current = false;
    setReloadKey((k) => k + 1);
  };

  // Данные на экране соответствуют выбранным фильтрам (иначе выгрузка собрала бы файл по прежнему периоду)
  const isFresh = Boolean(
    report && pharmacy &&
    report.pharmacy.username === pharmacy.username &&
    report.startDate === startDate &&
    report.endDate === endDate
  );
  const actionsReady = isFresh && !loading && !exporting && Boolean(report && report.rows.length > 0);

  const documentRows = useMemo(() => (report ? toDocumentRows(report.rows) : []), [report]);
  const documentUser = useMemo(
    () => (report ? { username: report.pharmacy.username, pharmacyNum: String(report.pharmacy.pharmacyNum) } : null),
    [report]
  );

  const audit = (kind: SummaryAuditKind) => {
    if (!report) return;
    auditSummary(kind, report.pharmacy.username, { startDate: report.startDate, endDate: report.endDate });
  };

  const runExport = async (kind: SummaryAuditKind, task: (data: SummaryResponse) => Promise<void>) => {
    if (!report || exporting) return;
    setExporting(true);
    try {
      await task(report);
      audit(kind);
    } catch (err) {
      console.error('Ошибка выгрузки в Excel:', err);
      notify.error('Не удалось сформировать файл Excel');
    } finally {
      setExporting(false);
    }
  };

  const handleExcel = () => runExport('excel', exportSummaryExcel);

  const handleCustomExport = async () => {
    const names = selectedColumns;
    setColumnsOpen(false);
    await runExport('excel_custom', (data) => exportSummaryExcelColumns(data, names));
  };

  const toggleColumn = (name: string) => {
    setSelectedColumns((prev) => (prev.indexOf(name) >= 0 ? prev.filter((n) => n !== name) : prev.concat(name)));
  };

  const selectableFields = useMemo(
    () => (report ? report.fields.filter((f) => f.name !== DATE_FIELD) : []),
    [report]
  );

  const renderBody = () => {
    if (usersError) {
      return (
        <EditEmptyState
          icon={<ErrorOutlineIcon />}
          title="Не удалось загрузить список аптек"
          text={usersError}
          action={<Button onClick={reloadUsers} sx={{ textTransform: 'none', fontWeight: 600 }}>Повторить</Button>}
        />
      );
    }
    if (usersData && usersData.users.length === 0) {
      return (
        <EditEmptyState
          icon={<TableChartOutlinedIcon />}
          title="Нет доступных аптек"
          text="Для вашей роли в сводном отчете нет аптек. Обратитесь к администратору."
        />
      );
    }
    if (!pharmacy) {
      return (
        <EditEmptyState
          icon={<TableChartOutlinedIcon />}
          title="Выберите аптеку"
          text="Укажите аптеку и период, чтобы увидеть сводный отчет."
        />
      );
    }
    if (!periodOk) {
      const reversed = isPeriodValid(startDate, startDate) && isPeriodValid(endDate, endDate) && startDate > endDate;
      return (
        <EditEmptyState
          icon={<EventBusyIcon />}
          title="Проверьте период"
          text={reversed ? 'Начальная дата позже конечной.' : 'Укажите начальную и конечную даты.'}
        />
      );
    }
    if (error) {
      return (
        <EditEmptyState
          icon={<ErrorOutlineIcon />}
          title="Не удалось загрузить отчет"
          text={error}
          action={<Button onClick={handleRetry} sx={{ textTransform: 'none', fontWeight: 600 }}>Повторить</Button>}
        />
      );
    }
    if (!report) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
          <CircularProgress size={32} />
        </Box>
      );
    }
    if (report.rows.length === 0) {
      return (
        <EditEmptyState
          icon={<EventBusyIcon />}
          title="За выбранный период данных нет"
          text="В отчетах этой аптеки нет записей между указанными датами."
        />
      );
    }
    return <SummaryTable fields={report.fields} rows={report.rows} totals={report.totals} stale={!isFresh} />;
  };

  const showActions = canExportExcel || canCashReport || canBankReport;

  return (
    <ReportSecurityWrapper protectCopy={canProtectCopy} username={pharmacy ? pharmacy.username : undefined}>
      <Box>
        <Paper
          elevation={0}
          sx={{ p: 2, mb: 2, border: BORDER, borderRadius: '12px', display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}
        >
          <PharmacyAutocomplete
            options={usersData ? usersData.users : []}
            value={pharmacy}
            onChange={handlePharmacyChange}
            loading={usersLoading}
          />
          <TextField
            size="small"
            label="Начальная дата"
            type="date"
            value={startDate}
            onChange={handleStartChange}
            error={!periodOk && startDate !== '' && endDate !== ''}
            InputLabelProps={{ shrink: true }}
            inputProps={{ min: MIN_REPORT_DATE, max: today }}
            sx={{ minWidth: 165 }}
          />
          <TextField
            size="small"
            label="Конечная дата"
            type="date"
            value={endDate}
            onChange={handleEndChange}
            error={!periodOk && startDate !== '' && endDate !== ''}
            InputLabelProps={{ shrink: true }}
            inputProps={{ min: MIN_REPORT_DATE, max: today }}
            sx={{ minWidth: 165 }}
          />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Button size="small" onClick={() => handlePreset(currentMonthPeriod())} sx={{ textTransform: 'none', fontWeight: 600, color: '#475569' }}>
              Текущий месяц
            </Button>
            <Button size="small" onClick={() => handlePreset(prevMonthPeriod())} sx={{ textTransform: 'none', fontWeight: 600, color: '#475569' }}>
              Прошлый месяц
            </Button>
            <Tooltip title="Обновить данные" arrow>
              <span>
                <IconButton size="small" onClick={handleRefresh} disabled={!pharmacy || !periodOk || loading} sx={{ color: '#64748b' }}>
                  <RefreshIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
          <Box sx={{ flexGrow: 1 }} />
          {usersData && <ScopeChip scope={usersData.scope} />}
        </Paper>

        <Box sx={{ height: 2, mb: 1, mx: 0.5 }}>
          {loading && <LinearProgress sx={{ height: 2, borderRadius: 1 }} />}
        </Box>

        {(showActions || (report && isFresh)) && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, mb: 1.5, minHeight: 36 }}>
            <Typography sx={{ fontSize: '0.84rem', color: '#64748b' }}>
              {report && isFresh
                ? `${report.pharmacy.fullName ? `Сотрудник: ${report.pharmacy.fullName}` : 'Сотрудник не указан'} · ${formatDateRu(report.startDate)} – ${formatDateRu(report.endDate)} · записей: ${report.rows.length}`
                : ''}
            </Typography>
            <Box sx={{ flexGrow: 1 }} />
            {canExportExcel && (
              <Button
                variant="contained"
                onClick={handleExcel}
                disabled={!actionsReady}
                startIcon={exporting ? <CircularProgress size={16} color="inherit" /> : <FileDownloadIcon />}
                sx={{
                  backgroundColor: '#10b981',
                  textTransform: 'none',
                  fontWeight: 600,
                  borderRadius: '8px',
                  boxShadow: 'none',
                  '&:hover': { backgroundColor: '#059669', boxShadow: 'none' }
                }}
              >
                Excel
              </Button>
            )}
            {canExportExcel && (
              <Button
                variant="outlined"
                onClick={() => setColumnsOpen(true)}
                disabled={!actionsReady}
                startIcon={<TuneIcon />}
                sx={ACTION_BUTTON_SX}
              >
                Выбор колонок
              </Button>
            )}
            {canCashReport && (
              <CashReport
                tableData={documentRows}
                selectedUser={documentUser}
                startDate={report ? report.startDate : startDate}
                endDate={report ? report.endDate : endDate}
                employeeName={report ? report.pharmacy.fullName : null}
                disabled={!actionsReady}
                onGenerated={() => audit('cash')}
              />
            )}
            {canBankReport && (
              <BankReport
                tableData={documentRows}
                selectedUser={documentUser}
                startDate={report ? report.startDate : startDate}
                endDate={report ? report.endDate : endDate}
                employeeName={report ? report.pharmacy.fullName : null}
                disabled={!actionsReady}
                onGenerated={() => audit('bank')}
              />
            )}
          </Box>
        )}

        {renderBody()}
      </Box>

      <ConfirmDialog
        open={columnsOpen}
        title="Колонки для выгрузки"
        confirmText="Сформировать"
        confirmDisabled={selectedColumns.length === 0}
        onConfirm={handleCustomExport}
        onClose={() => setColumnsOpen(false)}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <Typography sx={{ flex: 1, fontSize: '0.84rem', color: '#64748b' }}>
            Дата включается всегда. Выбрано колонок: {selectedColumns.length} из {selectableFields.length}
          </Typography>
          <Button size="small" onClick={() => setSelectedColumns(selectableFields.map((f) => f.name))} sx={{ textTransform: 'none', fontWeight: 600 }}>
            Выбрать все
          </Button>
          <Button size="small" onClick={() => setSelectedColumns([])} sx={{ textTransform: 'none', fontWeight: 600, color: '#475569' }}>
            Снять все
          </Button>
        </Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, columnGap: 2, border: BORDER, borderRadius: '8px', px: 1.5, py: 0.5 }}>
          {selectableFields.map((f) => (
            <FormControlLabel
              key={f.name}
              control={<Checkbox size="small" checked={selectedColumns.indexOf(f.name) >= 0} onChange={() => toggleColumn(f.name)} />}
              label={<Typography sx={{ fontSize: '0.86rem', color: '#1e293b' }}>{f.label}</Typography>}
            />
          ))}
        </Box>
      </ConfirmDialog>
    </ReportSecurityWrapper>
  );
};

export default PeriodReport;
